import React from 'react';

export const updated = 'September 22, 2026';

const privacyContentEn = (
  <>
    <h2>Privacy Policy</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <p>
      OnyxUP ("we", "us", "our"), developed by Halil Yılmaz, respects the privacy of users of the Social Fridge
      Magnets mobile application (the "App"). This Privacy Policy explains what data we collect, how we use it, and
      your rights.
    </p>

    <h2>1. Data We Collect</h2>
    <ul>
      <li><strong>Account information</strong> (username, and optional Sign in with Apple identifier) — Account creation, login, Room membership.</li>
      <li><strong>Photos you upload</strong> — Generating cutout-style magnet stickers for your fridge; visible only to you, or to your Room's members if placed on a shared fridge.</li>
      <li><strong>App activity data</strong> (fridges, magnet placements, Room membership, chosen fridge color, location tags you add to pin stickers) — Operating the fridge and Room features, syncing shared fridges in real time.</li>
      <li><strong>Device and diagnostic data</strong> (device model, iOS version, crash logs) — Troubleshooting and performance improvement.</li>
      <li><strong>Subscription/purchase data</strong> (transaction identifier and subscription status via RevenueCat) — Verifying access to Pro features.</li>
    </ul>

    <p><strong>Location Data:</strong></p>
    <p>
      If you choose to add a location-based pin sticker to your fridge, we process the location you select for that
      sticker. Location data is only collected when you actively choose to attach it to a sticker; we do not track
      your location in the background.
    </p>

    <h2>2. How Data Is Shared</h2>
    <ul>
      <li><strong>Room members:</strong> If you join or create a shared Room fridge, your username, magnet stickers, and pin stickers are visible to the other members of that Room (up to 5 members).</li>
      <li><strong>Service providers:</strong> We share limited data with the third-party providers listed below, solely to operate the App's core features.</li>
    </ul>
    <p>We do not sell your personal data to third parties for advertising purposes.</p>

    <h2>3. Third-Party Services Used</h2>
    <ul>
      <li><strong>RevenueCat</strong> — Subscription and in-app purchase management and validation.</li>
      <li><strong>Apple</strong> (App Store, Sign in with Apple, CloudKit/iCloud) — App distribution, payment processing, account authentication, and data sync.</li>
    </ul>

    <h2>4. Data Retention</h2>
    <p>
      We retain your data for as long as your account is active and as necessary to comply with our legal
      obligations. When you delete your account, your associated personal data is permanently removed from our
      systems within a reasonable period.
    </p>

    <h2>5. Your Rights</h2>
    <ul>
      <li>The right to access and correct your data</li>
      <li>The right to request deletion of your data (available instantly via the in-app account deletion feature)</li>
      <li>The right to object to certain processing of your data</li>
    </ul>
    <p>You can exercise these rights by contacting us at <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>

    <h2>6. Children's Privacy</h2>
    <p>
      The App does not knowingly collect personal data from children under the age of 13 (or the applicable minimum
      age in your region). If we learn that we have collected such data, we will delete it promptly.
    </p>

    <h2>7. Data Security</h2>
    <p>
      We use industry-standard technical and organizational measures to protect your data. However, no method of
      transmission over the internet is 100% secure.
    </p>

    <h2>8. Regional Differences</h2>
    <p>The App functions consistently across all regions, with no region-specific differences in data processing practices.</p>

    <h2>9. Changes to This Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. Changes will be posted on this page, and the "Last
      updated" date will be revised accordingly.
    </p>

    <h2>10. Contact</h2>
    <p>Privacy questions can be sent to: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

const termsContentEn = (
  <>
    <h2>Terms of Use</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <p>
      These Terms of Use ("Terms") govern your access to and use of the Social Fridge Magnets mobile application
      (the "App"), provided by OnyxUP ("we", "us", "our"), developed by Halil Yılmaz. By downloading, installing, or
      using the App, you agree to be bound by these Terms.
    </p>

    <h2>1. Description of the App</h2>
    <p>
      Social Fridge Magnets lets you turn your photos into cutout-style stickers ("magnets") and place them on a
      personal virtual fridge. Pro subscribers can also create and join shared fridges ("Rooms") with up to 5
      members, where all members can place magnets, add location-based pin stickers, and change the shared fridge's
      color in real time. Users can export their fridge as a PNG image for sharing on social media.
    </p>

    <h2>2. Accounts and Account Deletion</h2>
    <ul>
      <li>Creating a fridge and joining or creating Rooms requires a username-based account.</li>
      <li>You are responsible for maintaining the confidentiality of your account and for all activity under it.</li>
      <li>You may permanently delete your account and associated data at any time from within the App's settings.</li>
    </ul>

    <h2>3. User Content and Conduct</h2>
    <p>
      Photos you upload are converted into magnet stickers ("User Content"). On a personal fridge, User Content is
      visible only to you. On a shared Room fridge, User Content and magnet placements are visible to all members of
      that Room.
    </p>
    <ul>
      <li>You represent that you own or have the necessary rights to any photo you upload, and that it does not depict another identifiable person without their consent, and is not unlawful, obscene, harassing, hateful, or infringing on the rights of others.</li>
      <li>You can report inappropriate content or a member's behavior within a Room, and you can block a member so their content and future invites no longer reach you. Reported content is reviewed by us, and we may remove content or suspend or terminate the associated account where warranted.</li>
      <li>You may not use the App to harass, bully, spam, or engage in unlawful activity, or to invite or add anyone to a Room without their consent.</li>
    </ul>

    <h2>4. Subscriptions (Auto-Renewable)</h2>
    <p>Social Fridge Magnets Pro is offered as an auto-renewable subscription.</p>
    <ul>
      <li>Subscription title, duration, and price are displayed on the in-app purchase screen and on the App Store product page before you subscribe.</li>
      <li>Payment is charged to your Apple ID account at confirmation of purchase.</li>
      <li>Your subscription automatically renews for the same duration and at the then-current price unless auto-renewal is turned off at least 24 hours before the end of the current period.</li>
      <li>Your account will be charged for renewal within 24 hours prior to the end of the current period.</li>
      <li>You can manage or cancel your subscription at any time in your Apple ID Account Settings, after purchase.</li>
      <li>Any unused portion of a subscription period is non-refundable, except where required by applicable consumer protection law.</li>
      <li>Prices may vary by region and are always shown on the purchase screen before you buy.</li>
    </ul>

    <h2>5. Intellectual Property</h2>
    <p>
      All design, code, graphics, and branding within the App belong to OnyxUP or are used under license. You retain
      ownership of your User Content, but by uploading it you grant us a limited license to process it (e.g., to
      generate a magnet sticker cutout and display it to the intended recipients, such as your Room members) solely
      to operate the App's features.
    </p>

    <h2>6. Disclaimer of Warranties; Limitation of Liability</h2>
    <p>
      The App is provided "as is." We do not warrant that the App will operate uninterrupted or error-free. To the
      maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages
      arising from your use of the App.
    </p>

    <h2>7. Termination</h2>
    <p>We may suspend or terminate your account if you violate these Terms. You may delete your account at any time from within the App.</p>

    <h2>8. Changes to These Terms</h2>
    <p>We may update these Terms from time to time. Material changes will be announced via an in-app notice or an updated "Last updated" date.</p>

    <h2>9. Governing Law</h2>
    <p>These Terms are governed by the laws of the Republic of Türkiye, without regard to conflict-of-law principles.</p>

    <h2>10. Contact</h2>
    <p>Questions about these Terms can be sent to: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>

    <p><em>See also: Apple's Standard End User License Agreement (EULA), which applies to this App in addition to these Terms.</em></p>
  </>
);

const privacyContentTr = (
  <>
    <h2>Gizlilik Politikası</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <p>
      Halil Yılmaz tarafından geliştirilen OnyxUP ("biz", "bizim"), Social Fridge Magnets mobil uygulamasının
      ("Uygulama") kullanıcılarının gizliliğine değer verir. Bu Gizlilik Politikası, hangi verileri topladığımızı,
      bunları nasıl kullandığımızı ve haklarınızı açıklar.
    </p>

    <h2>1. Topladığımız Veriler</h2>
    <ul>
      <li><strong>Hesap bilgileri</strong> (kullanıcı adı ve isteğe bağlı Apple ile Oturum Aç kimliği) — Hesap oluşturma, giriş, Oda üyeliği.</li>
      <li><strong>Yüklediğiniz fotoğraflar</strong> — Buzdolabınız için kesme şeklinde magnet çıkartmaları oluşturma; yalnızca size, ya da paylaşımlı bir buzdolabına yerleştirilmişse Odanızın üyelerine görünür.</li>
      <li><strong>Uygulama etkinlik verileri</strong> (buzdolapları, magnet yerleşimleri, Oda üyeliği, seçilen buzdolabı rengi, çıkartmalara eklediğiniz konum etiketleri) — Buzdolabı ve Oda özelliklerini işletme, paylaşımlı buzdolaplarını gerçek zamanlı senkronize etme.</li>
      <li><strong>Cihaz ve tanılama verileri</strong> (cihaz modeli, iOS sürümü, çökme kayıtları) — Sorun giderme ve performans iyileştirme.</li>
      <li><strong>Abonelik/satın alma verileri</strong> (RevenueCat üzerinden işlem kimliği ve abonelik durumu) — Pro özelliklere erişimi doğrulama.</li>
    </ul>

    <p><strong>Konum Verileri:</strong></p>
    <p>
      Buzdolabınıza konum tabanlı bir çıktırma stickerı eklemeyi seçerseniz, o sticker için seçtiğiniz konumu
      işleriz. Konum verileri yalnızca bunu bir stickera eklemeyi aktif olarak seçtiğinizde toplanır; konumunuzu
      arka planda takip etmeyiz.
    </p>

    <h2>2. Veriler Nasıl Paylaşılır</h2>
    <ul>
      <li><strong>Oda üyeleri:</strong> Paylaşımlı bir Oda buzdolabına katılır veya oluşturursanız, kullanıcı adınız, magnet çıkartmalarınız ve konum stickerlarınız o Odanın diğer üyelerine (en fazla 5 üye) görünür.</li>
      <li><strong>Hizmet sağlayıcıları:</strong> Uygulamanın temel özelliklerini işletmek amacıyla aşağıda listelenen üçüncü taraf sağlayıcılarla sınırlı veri paylaşırız.</li>
    </ul>
    <p>Kişisel verilerinizi reklam amacıyla üçüncü taraflara satmayız.</p>

    <h2>3. Kullanılan Üçüncü Taraf Hizmetleri</h2>
    <ul>
      <li><strong>RevenueCat</strong> — Abonelik ve uygulama içi satın alma yönetimi ve doğrulaması.</li>
      <li><strong>Apple</strong> (App Store, Apple ile Oturum Aç, CloudKit/iCloud) — Uygulama dağıtımı, ödeme işleme, hesap kimlik doğrulama ve veri senkronizasyonu.</li>
    </ul>

    <h2>4. Veri Saklama</h2>
    <p>
      Verilerinizi hesabınız aktif olduğu sürece ve yasal yükümlülüklerimize uymak için gerekli olduğu kadar
      saklarız. Hesabınızı sildiğinizde, ilişkili kişisel verileriniz makul bir süre içinde sistemlerimizden kalıcı
      olarak kaldırılır.
    </p>

    <h2>5. Haklarınız</h2>
    <ul>
      <li>Verilerinize erişme ve düzeltme hakkı</li>
      <li>Verilerinizin silinmesini talep etme hakkı (uygulama içi hesap silme özelliği ile anında kullanılabilir)</li>
      <li>Verilerinizin belirli işlemlerine itiraz etme hakkı</li>
    </ul>
    <p>Bu hakları <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a> adresinden bizimle iletişime geçerek kullanabilirsiniz</p>

    <h2>6. Çocukların Gizliliği</h2>
    <p>
      Uygulama, 13 yaşın altındaki çocuklardan (veya bölgenizdeki geçerli asgari yaştan) bilerek kişisel veri
      toplamaz. Böyle bir veri topladığımızı öğrenirsek, bunu derhal sileriz.
    </p>

    <h2>7. Veri Güvenliği</h2>
    <p>
      Verilerinizi korumak için sektör standardı teknik ve organizasyonel tedbirler kullanırız. Ancak internet
      üzerinden hiçbir iletim yöntemi %100 güvenli değildir.
    </p>

    <h2>8. Bölgesel Farklar</h2>
    <p>Uygulama tüm bölgelerde tutarlı şekilde çalışır; veri işleme uygulamalarında bölgeye özel bir fark yoktur.</p>

    <h2>9. Bu Politikadaki Değişiklikler</h2>
    <p>
      Bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Değişiklikler bu sayfada yayınlanacak ve "Son
      güncelleme" tarihi buna göre revize edilecektir.
    </p>

    <h2>10. İletişim</h2>
    <p>Gizlilik soruları şu adrese gönderilebilir: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

const termsContentTr = (
  <>
    <h2>Kullanım Şartları</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <p>
      Bu Kullanım Şartları ("Şartlar"), Halil Yılmaz tarafından geliştirilen OnyxUP ("biz", "bizim") tarafından
      sağlanan Social Fridge Magnets mobil uygulamasına ("Uygulama") erişiminizi ve kullanımınızı düzenler.
      Uygulamayı indirerek, kurarak veya kullanarak bu Şartlara bağlı kalmayı kabul edersiniz.
    </p>

    <h2>1. Uygulamanın Tanımı</h2>
    <p>
      Social Fridge Magnets, fotoğraflarınızı kesme şeklinde çıkartmalara ("magnet") dönüştürmenize ve bunları
      kişisel bir sanal buzdolabına yerleştirmenize olanak tanır. Pro aboneler, en fazla 5 üyeye kadar paylaşımlı
      buzdolapları ("Odalar") oluşturabilir ve katılabilir; burada tüm üyeler magnet yerleştirebilir, konum tabanlı
      çıktırma stickerları ekleyebilir ve paylaşımlı buzdolabının rengini gerçek zamanlı değiştirebilir. Kullanıcılar
      buzdolaplarını sosyal medyada paylaşmak için PNG görseli olarak dışa aktarabilir.
    </p>

    <h2>2. Hesaplar ve Hesap Silme</h2>
    <ul>
      <li>Bir buzdolabı oluşturmak ve Odalara katılmak veya Oda oluşturmak, kullanıcı adı tabanlı bir hesap gerektirir.</li>
      <li>Hesabınızın gizliliğini korumaktan ve hesabınız altındaki tüm etkinlikten siz sorumlusunuz.</li>
      <li>Hesabınızı ve ilişkili verilerinizi Uygulamanın ayarlarından istediğiniz zaman kalıcı olarak silebilirsiniz.</li>
    </ul>

    <h2>3. Kullanıcı İçeriği ve Davranış</h2>
    <p>
      Yüklediğiniz fotoğraflar magnet çıkartmalarına ("Kullanıcı İçeriği") dönüştürülür. Kişisel bir buzdolabında,
      Kullanıcı İçeriği yalnızca size görünür. Paylaşımlı bir Oda buzdolabında, Kullanıcı İçeriği ve magnet
      yerleşimleri o Odanın tüm üyelerine görünür.
    </p>
    <ul>
      <li>Yüklediğiniz herhangi bir fotoğrafın sahibi olduğunuzu veya gerekli haklara sahip olduğunuzu, başka bir tanımlanabilir kişiyi onayı olmadan göstermediğini ve yasa dışı, müstehcen, taciz edici, nefret içerikli veya başkalarının haklarını ihlal eden nitelikte olmadığını beyan edersiniz.</li>
      <li>Bir Oda içindeki uygunsuz içeriği veya bir üyenin davranışını bildirebilir ve bir üyeyi engelleyerek içeriklerinin ve gelecekteki davetlerinin size ulaşmasını önleyebilirsiniz. Bildirilen içerik bizim tarafımızdan incelenir ve gerektiğinde içeriği kaldırabilir veya ilişkili hesabı askıya alabilir ya da sonlandırabiliriz.</li>
      <li>Uygulamayı taciz etmek, zorbalık yapmak, spam göndermek veya yasa dışı faaliyette bulunmak, ya da onayı olmadan birini bir Odaya davet etmek veya eklemek için kullanamazsınız.</li>
    </ul>

    <h2>4. Abonelikler (Otomatik Yenilenen)</h2>
    <p>Social Fridge Magnets Pro, otomatik yenilenen bir abonelik olarak sunulur.</p>
    <ul>
      <li>Abonelik başlığı, süresi ve fiyatı, abone olmadan önce uygulama içi satın alma ekranında ve App Store ürün sayfasında görüntülenir.</li>
      <li>Ödeme, satın alma onaylandığında Apple ID hesabınızdan tahsil edilir.</li>
      <li>Aboneliğiniz, geçerli dönemin sonundan en az 24 saat önce otomatik yenileme kapatılmadığı takdirde aynı süre ve o zamanki geçerli fiyattan otomatik olarak yenilenir.</li>
      <li>Hesabınız, geçerli dönemin sonundan 24 saat öncesine kadar yenileme için ücretlendirilecektir.</li>
      <li>Aboneliğinizi satın alma sonrası Apple ID Hesap Ayarlarınızdan istediğiniz zaman yönetebilir veya iptal edebilirsiniz.</li>
      <li>Geçerli tüketici koruma yasasının gerektirdiği durumlar dışında, bir abonelik döneminin kullanılmayan kısmı için iade yapılmaz.</li>
      <li>Fiyatlar bölgeye göre değişebilir ve satın almadan önce her zaman satın alma ekranında gösterilir.</li>
    </ul>

    <h2>5. Fikri Mülkiyet</h2>
    <p>
      Uygulama içindeki tüm tasarım, kod, grafik ve marka öğeleri OnyxUP'a aittir veya lisans altında kullanılır.
      Kullanıcı İçeriğinizin mülkiyetini elinde tutarsınız, ancak onu yükleyerek bize bunu işlemek için (örneğin bir
      magnet çıkartma kesimi oluşturmak ve Oda üyeleriniz gibi hedeflenen alıcılara göstermek için) Uygulamanın
      özelliklerini işletmek amacıyla sınırlı bir lisans verirsiniz.
    </p>

    <h2>6. Garanti Feragati; Sorumluluğun Sınırlandırılması</h2>
    <p>
      Uygulama "olduğu gibi" sağlanır. Uygulamanın kesintisiz veya hatasız çalışacağını garanti etmeyiz. Yasaların
      izin verdiği azami ölçüde, Uygulamayı kullanımınızdan kaynaklanan hiçbir dolaylı, arızi veya sonuç olarak
      ortaya çıkan zarardan sorumlu değiliz.
    </p>

    <h2>7. Fesih</h2>
    <p>Bu Şartları ihlal etmeniz durumunda hesabınızı askıya alabilir veya sonlandırabiliriz. Hesabınızı Uygulama içinden istediğiniz zaman silebilirsiniz.</p>

    <h2>8. Bu Şartlardaki Değişiklikler</h2>
    <p>Bu Şartları zaman zaman güncelleyebiliriz. Önemli değişiklikler uygulama içi bir bildirim veya güncellenmiş bir "Son güncelleme" tarihiyle duyurulacaktır.</p>

    <h2>9. Geçerli Hukuk</h2>
    <p>Bu Şartlar, kanunlar ihtilafı kurallarına bakılmaksızın Türkiye Cumhuriyeti yasalarına göre yönetilir.</p>

    <h2>10. İletişim</h2>
    <p>Bu Şartlar hakkında sorularınız şu adrese gönderilebilir: <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>

    <p><em>Ayrıca bakınız: Bu Şartlara ek olarak bu Uygulamaya uygulanan Apple'ın Standart Son Kullanıcı Lisans Sözleşmesi (EULA).</em></p>
  </>
);

export const en = { privacyContent: privacyContentEn, termsContent: termsContentEn };
export const tr = { privacyContent: privacyContentTr, termsContent: termsContentTr };
